import {
  ChangeEvent,
  FunctionComponent,
  useReducer,
  useState,
  useContext
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  TextFormat as TextFormatIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Person as UserIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import {
  ThemeProvider,
  createMuiTheme,
  createTheme,
  makeStyles
} from "@material-ui/core/styles";
import produce from "immer";
import MaterialTable, { Column } from "@material-table/core";
import { Authenticated, hasRole } from "../../../../models/User";
import {
  Day,
  RawSchedule,
  Schedule,
  StoreConfiguration,
  StorePickupBox,
  StoreState,
  StoreWriteModel
} from "../../../../models/Store";
import * as StoreApi from "../../../../api/storeApi";
import * as TranslationApi from "../../../../api/translationApi";
import { Language, TranslationV2 } from "../../../../models/Translation";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { useLoadIfEmpty } from "../../../../hooks/useLoadIfEmpty";
import { thunkFetchCurrentTenantLanguages } from "../../../../store/tenants/actions";
import { getTranslationValuesOrDefault } from "../../../../utils";
import { get } from "../../../../api/api";
import StoreMap from "./StoreMap";
import { ScheduleCard } from "./ScheduleCard";
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";
import { UserContext } from "../../../../UserProvider";
import { useI18n } from "../../../../hooks/useI18n";
import { useUpsertStoreMutation } from "../../../../queries/stores";

export const WEEKDAYS: Day[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export interface AccessRestrictionSchedule {
  from: string | null;
  to: string | null;
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2"
    }
  },
  overrides: {
    MuiDivider: {
      root: {
        margin: "16px 0"
      }
    }
  }
});

const useStyles = makeStyles(theme => {
  const user = useContext(UserContext);
  const primaryColor = user.tenant.configuration?.primaryColor || "#1976d2";

  return {
    root: {
      padding: theme.spacing(2),
      maxWidth: 1200,
      margin: "0 auto"
    },
    header: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      marginBottom: theme.spacing(2)
    },
    tabs: {
      marginBottom: theme.spacing(2)
    },
    tabContent: {
      padding: theme.spacing(2, 0)
    },
    section: {
      marginBottom: theme.spacing(3)
    },
    sectionTitle: {
      fontWeight: 500,
      marginBottom: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      fontSize: "1.1rem",
      "& svg": {
        marginRight: theme.spacing(1),
        fontSize: "1.2rem",
        color: primaryColor
      }
    },
    inputField: {
      marginBottom: theme.spacing(1.5)
    },
    compactField: {
      marginBottom: theme.spacing(0.5)
    },
    mapContainer: {
      height: 240,
      marginBottom: theme.spacing(1)
    },
    saveButton: {
      marginRight: theme.spacing(1)
    },
    restrictionOptions: {
      marginLeft: theme.spacing(2),
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5),
      backgroundColor: "#f5f5f5",
      borderRadius: 4
    },
    infoText: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      marginTop: theme.spacing(0.5)
    },
    jsonEditor: {
      marginTop: theme.spacing(2)
    },
    dayContainer: {
      marginBottom: theme.spacing(1)
    },
    timeSlotContainer: {
      marginBottom: theme.spacing(0.5)
    },
    compactDivider: {
      margin: theme.spacing(1, 0)
    },
    timeSlotsGrid: {
      display: "flex",
      flexWrap: "wrap",
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5)
    },
    timeSlot: {
      display: "flex",
      alignItems: "center",
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    dayLabel: {
      width: 100,
      fontWeight: 500,
      marginRight: theme.spacing(2),
      color: primaryColor
    },
    addButton: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5)
    },
    timeField: {
      width: 120
    },
    compactGrid: {
      display: "flex",
      flexWrap: "wrap",
      "& > *": {
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1.5)
      }
    },
    activeStatus: {
      backgroundColor: `${primaryColor}14`,
      color: primaryColor,
      padding: theme.spacing(0.5, 1),
      borderRadius: 4,
      fontWeight: 500,
      fontSize: 14,
      display: "inline-block"
    }
  };
});

interface StoreEditorProps {
  currentUser: Authenticated;
  store: StoreWriteModel;
  isSuccessHandler: Function;
}

export const StoreEditor: FunctionComponent<StoreEditorProps> = ({
  currentUser,
  store,
  isSuccessHandler
}) => {
  const classes = useStyles();
  const user = useContext(UserContext);
  const primaryColor = user.tenant.configuration?.primaryColor || "#1976d2";
  const { t } = useI18n();

  const dynamicTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor
      }
    },
    overrides: {
      MuiDivider: {
        root: {
          margin: "16px 0"
        }
      }
    }
  });

  const [activeTab, setActiveTab] = useState(0);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const upsertStoreMutation = useUpsertStoreMutation();

  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const [state, setState] =
    useState<Omit<StoreWriteModel, "config" | "openingSchedule">>(store);
  const [schedule, setSchedule] = useState<Schedule>(
    convertRawSchedule(store.openingSchedule ?? {})
  );

  const [accessMode, setAccessMode] = useState(
    store.config.door?.verifiedUserTimeRestrictions
      ? "time"
      : store.config.door?.verifiedUserAgeLimit
      ? "age"
      : "all"
  );

  const [translation, setTranslation] = useState<TranslationV2>({
    id: `store.${store.id}.freetext`,
    values: {}
  });

  const [pickupBoxesState, setPickupBoxesState] = useState<{
    columns: Array<Column<StorePickupBox>>;
    data: StorePickupBox[];
  }>({
    columns: [
      { title: t("common.formFields.slot"), field: "slot", type: "numeric" },
      {
        title: t("common.formFields.terminalId"),
        field: "terminalId",
        type: "numeric"
      }
    ],
    data: store.pickupBoxes ?? []
  });

  const [isAddressChanged, setIsAddressChanged] = useState(false);
  const [config, dispatchConfigChange] = useReducer(
    configReducer,
    store.config
  );
  const [isAgeNumber, setIsAgeNumber] = useState(true);

  const handleOnChange = (store: Omit<StoreWriteModel, "config">) => {
    setState(store);
  };

  const handleAddressChange = async () => {
    const response = await get("/v1/stores/coordinates", {
      street: state.address.street,
      postalCode: state.address.postalCode,
      city: state.address.city
    });
    const location = await response.json();
    setState(
      produce(state, draft => {
        draft.location.latitude = location.latitude;
        draft.location.longitude = location.longitude;
      })
    );
    setIsAddressChanged(false);
    setSuccess(true);
  };

  const handleTranslationChange =
    (code: string) => (e: ChangeEvent<HTMLInputElement>) => {
      let translationValues = getTranslationValuesOrDefault(translation);
      translationValues[code] = e.target.value;
      setTranslation({
        ...translation,
        values: translationValues
      });
    };

  function handleRowUpdate(newPickupBox: StorePickupBox, oldPickupBox: any) {
    return new Promise(resolve => {
      const updated = [...pickupBoxesState.data];
      const index = oldPickupBox.tableData.id;
      updated[index] = newPickupBox;
      setPickupBoxesState({
        ...pickupBoxesState,
        data: [...updated]
      });
      handleOnChange({
        ...state,
        pickupBoxes: updated,
        pickupBoxesChanged: true
      });
      resolve(null);
    });
  }

  function handleRowAdd(newPickupBox: StorePickupBox) {
    return new Promise(resolve => {
      const pickupBoxes = [...pickupBoxesState.data, newPickupBox];
      setPickupBoxesState({
        ...pickupBoxesState,
        data: pickupBoxes
      });
      handleOnChange({
        ...state,
        pickupBoxes: pickupBoxes,
        pickupBoxesChanged: true
      });
      resolve(null);
    });
  }

  async function submitStore() {
    setSaving(true);
    try {
      await upsertStoreMutation.mutateAsync({
        storeId: state.id,
        writeModel: {
          ...state,
          config: config,
          openingSchedule: schedule
        } as any,
        translation
      });

      setSuccess(true);
      isSuccessHandler(true);
    } catch (error) {
      console.error("Error saving store:", error);
      setError(true);
      isSuccessHandler(false);
    } finally {
      setSaving(false);
    }
  }

  const isFormValid = !disabled({ ...state, config: config });

  const basicInfoTab = (
    <div className={classes.tabContent}>
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <BusinessIcon /> Store Details
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("common.formFields.storeName")}
              variant="outlined"
              className={classes.inputField}
              value={state.name}
              onChange={e =>
                handleOnChange({
                  ...state,
                  name: e.target.value
                })
              }
              required
            />
          </Grid>
        </Grid>

        <Divider />

        <Typography variant="subtitle1" style={{ margin: "16px 0" }}>
          Address
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street"
              variant="outlined"
              className={classes.inputField}
              value={state.address.street}
              onChange={e => {
                handleOnChange({
                  ...state,
                  address: {
                    ...state.address,
                    street: e.target.value
                  }
                });
                setIsAddressChanged(true);
              }}
              required
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label={t("common.formFields.postalCode")}
              variant="outlined"
              className={classes.inputField}
              value={state.address.postalCode}
              onChange={e => {
                handleOnChange({
                  ...state,
                  address: {
                    ...state.address,
                    postalCode: e.target.value
                  }
                });
                setIsAddressChanged(true);
              }}
              required
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="City"
              variant="outlined"
              className={classes.inputField}
              value={state.address.city}
              onChange={e => {
                handleOnChange({
                  ...state,
                  address: {
                    ...state.address,
                    city: e.target.value
                  }
                });
                setIsAddressChanged(true);
              }}
              required
            />
          </Grid>
        </Grid>

        {isAddressChanged && (
          <Box mt={2}>
            <Alert
              severity="info"
              action={
                <Button
                  color="primary"
                  size="small"
                  onClick={handleAddressChange}
                >
                  Update Coordinates
                </Button>
              }
            >
              Address has changed. Update the location coordinates?
            </Alert>
          </Box>
        )}
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <LocationIcon /> Location
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Latitude"
                  variant="outlined"
                  className={classes.compactField}
                  value={state.location.latitude}
                  onChange={e =>
                    handleOnChange({
                      ...state,
                      location: {
                        ...state.location,
                        latitude: Number(e.target.value)
                      }
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Longitude"
                  variant="outlined"
                  className={classes.compactField}
                  value={state.location.longitude}
                  onChange={e =>
                    handleOnChange({
                      ...state,
                      location: {
                        ...state.location,
                        longitude: Number(e.target.value)
                      }
                    })
                  }
                  required
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="textSecondary">
              You can click on the map to set the location
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className={classes.mapContainer}>
              <StoreMap
                lat={state.location.latitude}
                lng={state.location.longitude}
                onChange={({ lat, lng }) =>
                  handleOnChange(
                    produce(state, draft => {
                      draft.location.latitude = lat;
                      draft.location.longitude = lng;
                    })
                  )
                }
              />
            </div>
          </Grid>
        </Grid>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <TextFormatIcon /> Store Description
        </Typography>

        <Grid container spacing={3}>
          {tenantLanguages.map(lang => (
            <Grid item xs={12} md={6} key={lang.code}>
              <TextField
                fullWidth
                label={`Description (${lang.name})`}
                variant="outlined"
                multiline
                rows={3}
                value={translation?.values[lang.code] || ""}
                onChange={handleTranslationChange(lang.code)}
                className={classes.inputField}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );

  const accessControlTab = (
    <div className={classes.tabContent}>
      <Paper
        elevation={1}
        style={{ padding: theme.spacing(3), marginBottom: theme.spacing(3) }}
      >
        <Typography variant="h6" className={classes.sectionTitle}>
          <BusinessIcon style={{ color: primaryColor }} /> Store Status
        </Typography>

        <RadioGroup
          name="Status"
          value={state.state}
          onChange={e =>
            handleOnChange({
              ...state,
              state: e.target.value as StoreState
            })
          }
        >
          <FormControlLabel
            value="Opened"
            control={<Radio color="primary" />}
            label={
              <Box display="flex" alignItems="center">
                <span className={classes.activeStatus}>Open</span>
                <Typography style={{ marginLeft: 12 }}>
                  Store is available for customers
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="InSetup"
            control={<Radio color="primary" />}
            label={
              <Box display="flex" alignItems="center">
                <span
                  style={{
                    backgroundColor: "#f5f5f5",
                    color: "#757575",
                    padding: "4px 8px",
                    borderRadius: 4,
                    fontWeight: 500,
                    fontSize: 14,
                    display: "inline-block"
                  }}
                >
                  Maintenance
                </span>
                <Typography style={{ marginLeft: 12 }}>
                  Store is temporarily unavailable
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </Paper>

      <Paper
        elevation={1}
        style={{ padding: theme.spacing(3), marginBottom: theme.spacing(3) }}
      >
        <Typography variant="h6" className={classes.sectionTitle}>
          <UserIcon style={{ color: primaryColor }} /> User Access Settings
        </Typography>

        <Box mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.door?.onlyAllowVerifiedUsers || false}
                onChange={e =>
                  dispatchConfigChange({
                    type: "OnlyAllowVerifiedUsers",
                    value: e.target.checked
                  })
                }
                color="primary"
              />
            }
            label={
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                Only verified users allowed to access the store
              </Typography>
            }
          />
        </Box>

        {config.door?.onlyAllowVerifiedUsers && (
          <Box
            className={classes.restrictionOptions}
            style={{
              borderLeft: `4px solid ${primaryColor}`,
              borderRadius: "4px",
              backgroundColor: `${primaryColor}08` // Very light version with 3% opacity
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              style={{ fontWeight: 500 }}
            >
              Restriction Type
            </Typography>

            <RadioGroup
              value={accessMode}
              onChange={e => setAccessMode(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio color="primary" />}
                label={t("common.formFields.alwaysRequireVerification")}
              />

              <FormControlLabel
                value="age"
                control={<Radio color="primary" />}
                label="Age-based restriction"
              />

              <FormControlLabel
                value="time"
                control={<Radio color="primary" />}
                label="Time-based restriction"
              />
            </RadioGroup>

            {accessMode === "age" && (
              <Box mt={2} p={2} bgcolor="#f8f9fa" borderRadius={1}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      Minimum age to access the store:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">years</InputAdornment>
                        )
                      }}
                      value={config.door?.verifiedUserAgeLimit || ""}
                      onChange={e => {
                        const [value, isN] = handleNumberField(e.target.value);
                        setIsAgeNumber(isN);
                        dispatchConfigChange({
                          type: "VerifiedUserAgeLimit",
                          value: value
                        });

                        dispatchConfigChange({
                          type: "VerifiedUserTimeFrom",
                          value: null
                        });
                        dispatchConfigChange({
                          type: "VerifiedUserTimeTo",
                          value: null
                        });
                      }}
                      error={!isAgeNumber}
                      helperText={
                        !isAgeNumber ? "Please enter a valid number" : ""
                      }
                    />
                  </Grid>
                </Grid>

                <Typography variant="body2" className={classes.infoText}>
                  Users below this age will not be permitted to enter the store.
                </Typography>
              </Box>
            )}

            {accessMode === "time" && (
              <Box mt={2} p={2} bgcolor="#f8f9fa" borderRadius={1}>
                <Typography variant="body2" gutterBottom>
                  During these hours, only verified users can access the store:
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="From"
                      type="time"
                      variant="outlined"
                      size="small"
                      value={
                        config.door?.verifiedUserTimeRestrictions?.from || ""
                      }
                      onChange={e => {
                        dispatchConfigChange({
                          type: "VerifiedUserTimeFrom",
                          value: e.target.value
                        });

                        // Clear age limit when switching to time mode
                        dispatchConfigChange({
                          type: "VerifiedUserAgeLimit",
                          value: null
                        });
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    container
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography variant="body1">to</Typography>
                  </Grid>

                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="To"
                      type="time"
                      variant="outlined"
                      size="small"
                      value={
                        config.door?.verifiedUserTimeRestrictions?.to || ""
                      }
                      onChange={e =>
                        dispatchConfigChange({
                          type: "VerifiedUserTimeTo",
                          value: e.target.value
                        })
                      }
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="body2" className={classes.infoText}>
                  Outside these hours, all users can access the store according
                  to regular store policies.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Divider style={{ margin: "24px 0" }} />

        <Box mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.features?.otpForVerifiedUsers || false}
                onChange={e =>
                  dispatchConfigChange({
                    type: "OtpForVerifiedUsers",
                    value: e.target.checked
                  })
                }
                color="primary"
              />
            }
            label={
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                Verified users can access store using phone number login
              </Typography>
            }
          />
        </Box>
      </Paper>

      <Paper
        elevation={1}
        style={{ padding: theme.spacing(3), marginBottom: theme.spacing(3) }}
      >
        <Typography variant="h6" className={classes.sectionTitle}>
          <LocationIcon style={{ color: primaryColor }} /> Door Access Settings
        </Typography>

        <Box mt={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Typography variant="body2">
                Maximum distance to open the door:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">meters</InputAdornment>
                  )
                }}
                value={config.door?.openDoorRequiredDistance || ""}
                onChange={e => {
                  const [value, isN] = handleNumberField(e.target.value);
                  if (isN) {
                    dispatchConfigChange({
                      type: "OpenDoorRequiredDistance",
                      value: value
                    });
                  }
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" className={classes.infoText}>
            Users must be within this distance to open the store door
          </Typography>
        </Box>
      </Paper>

      <ScheduleCard value={schedule} onChange={setSchedule} />
    </div>
  );

  const technicalTab = (
    <div className={classes.tabContent}>
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Terminal Configuration
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("common.formFields.terminalId")}
              variant="outlined"
              type="number"
              value={config.door?.terminalId || ""}
              onChange={e => {
                const [value, isN] = handleNumberField(e.target.value);
                if (isN) {
                  dispatchConfigChange({
                    type: "TerminalId",
                    value: value
                  });
                }
              }}
            />
          </Grid>
        </Grid>
      </div>

      {hasRole(currentUser, ["Root"]) && (
        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <SettingsIcon /> Advanced Settings
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowJsonEditor(!showJsonEditor)}
          >
            {showJsonEditor ? "Hide JSON Editor" : "Show JSON Editor"}
          </Button>

          {showJsonEditor && (
            <div className={classes.jsonEditor}>
              <JSONInput
                id="jsonConfigEditor"
                placeholder={config}
                locale={locale}
                height="300px"
                width="100%"
                onChange={(jsonChange: any) =>
                  dispatchConfigChange({
                    type: "Config",
                    value: jsonChange.json
                  })
                }
              />

              <Typography variant="caption" color="textSecondary">
                Warning: Only edit this if you know what you're doing.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <ThemeProvider theme={dynamicTheme}>
      <div className={classes.root}>
        <div className={classes.header}>
          <Box>
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid || saving}
              startIcon={<SaveIcon />}
              className={classes.saveButton}
              onClick={submitStore}
            >
              {saving
                ? t("common.messages.pleaseWait")
                : t("common.actions.save")}
            </Button>
          </Box>
        </div>

        <Paper className={classes.tabs}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={t("common.tabs.basicInfo")} />
            <Tab label={t("common.tabs.accessControl")} />
            <Tab label={t("common.formFields.technical")} />
          </Tabs>
        </Paper>

        {activeTab === 0 && basicInfoTab}
        {activeTab === 1 && accessControlTab}
        {activeTab === 2 && technicalTab}

        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert onClose={() => setSuccess(false)} severity="success">
            {t("common.messages.changesSaved")}
          </Alert>
        </Snackbar>

        <Snackbar
          open={error}
          autoHideDuration={6000}
          onClose={() => setError(false)}
        >
          <Alert onClose={() => setError(false)} severity="error">
            {t("common.messages.errorOccurred")}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
};

type ConfigAction =
  | { type: "ClickNCollect"; value: boolean | null }
  | { type: "OtpForVerifiedUsers"; value: boolean | null }
  | { type: "OnlyAllowVerifiedUsers"; value: boolean | null }
  | { type: "VerifiedUserAgeLimit"; value: number | null }
  | { type: "VerifiedUserTimeFrom"; value: string | null }
  | { type: "VerifiedUserTimeTo"; value: string | null }
  | { type: "OpenDoorRequiredDistance"; value: number | null }
  | { type: "TerminalId"; value: number | null }
  | { type: "OpenHour"; value: string | null }
  | { type: "CloseHour"; value: string | null }
  | { type: "Config"; value: any };

const configReducer = (config: StoreConfiguration, action: ConfigAction) => {
  switch (action.type) {
    case "ClickNCollect":
      return produce(config, draftState => {
        draftState.features = draftState.features || {};
        if (action.value) draftState.features.clickNCollect = action.value;
        else delete draftState.features?.clickNCollect;
        if (draftState.features && !Object.keys(draftState.features).length)
          delete draftState.features;
      });
    case "OtpForVerifiedUsers":
      return produce(config, draftState => {
        draftState.features = draftState.features || {};
        if (action.value)
          draftState.features.otpForVerifiedUsers = action.value;
        else delete draftState.features?.otpForVerifiedUsers;
        if (draftState.features && !Object.keys(draftState.features).length)
          delete draftState.features;
      });
    case "OnlyAllowVerifiedUsers":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        if (action.value) draftState.door.onlyAllowVerifiedUsers = action.value;
        else {
          delete draftState.door?.onlyAllowVerifiedUsers;
          delete draftState.door?.verifiedUserAgeLimit;
          delete draftState.door?.verifiedUserTimeRestrictions;
        }
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "VerifiedUserAgeLimit":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        if (action.value) draftState.door.verifiedUserAgeLimit = action.value;
        else delete draftState.door?.verifiedUserAgeLimit;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "VerifiedUserTimeFrom":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        draftState.door.verifiedUserTimeRestrictions = draftState.door
          ?.verifiedUserTimeRestrictions || {
          from: null,
          to: null
        };
        draftState.door.verifiedUserTimeRestrictions.from = action.value;
        if (
          draftState.door?.verifiedUserTimeRestrictions?.from == null &&
          draftState.door?.verifiedUserTimeRestrictions?.to == null
        )
          delete draftState.door?.verifiedUserTimeRestrictions;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "VerifiedUserTimeTo":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        draftState.door.verifiedUserTimeRestrictions = draftState.door
          ?.verifiedUserTimeRestrictions || {
          from: null,
          to: null
        };
        draftState.door.verifiedUserTimeRestrictions.to = action.value;
        if (
          draftState.door?.verifiedUserTimeRestrictions?.from == null &&
          draftState.door?.verifiedUserTimeRestrictions?.to == null
        )
          delete draftState.door?.verifiedUserTimeRestrictions;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "OpenDoorRequiredDistance":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        if (action.value)
          draftState.door.openDoorRequiredDistance = action.value;
        else delete draftState.door?.openDoorRequiredDistance;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "TerminalId":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        if (action.value) draftState.door.terminalId = action.value;
        else delete draftState.door?.terminalId;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "OpenHour":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        draftState.door.closingHours = draftState.door?.closingHours || {
          from: null,
          to: null
        };
        draftState.door.closingHours.to = action.value;
        if (
          draftState.door?.closingHours?.from == null &&
          draftState.door?.closingHours?.to == null
        )
          delete draftState.door?.closingHours;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "CloseHour":
      return produce(config, draftState => {
        draftState.door = draftState.door || {};
        draftState.door.closingHours = draftState.door?.closingHours || {
          from: null,
          to: null
        };
        draftState.door.closingHours.from = action.value;
        if (
          draftState.door?.closingHours?.from == null &&
          draftState.door?.closingHours?.to == null
        )
          delete draftState.door?.closingHours;
        if (draftState.door && !Object.keys(draftState.door).length)
          delete draftState.door;
      });
    case "Config":
      try {
        return typeof action.value === "string"
          ? JSON.parse(action.value)
          : action.value;
      } catch (e) {
        console.error("Config syntax error", e);
        return config;
      }
    default:
      console.error("Unknown action", action);
      return config;
  }
};

const handleNumberField = (
  targetValue: string
): [number, true] | [null, false] => {
  const value = Number(targetValue);
  if (!isNaN(value) || targetValue === "") {
    return [value, true];
  } else {
    return [null, false];
  }
};

function convertRawSchedule(rawSchedule: RawSchedule): Schedule {
  let schedule: Schedule = {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: []
  };

  for (const key in rawSchedule) {
    const capitalKey = key[0].toUpperCase() + key.slice(1);
    schedule[capitalKey as Day] = rawSchedule[key];
  }

  return schedule as Schedule;
}

const disabled = (state: StoreWriteModel) => {
  return (
    !state.name ||
    !state.address.street ||
    !state.address.postalCode ||
    !state.address.city ||
    !state.location.latitude ||
    !state.location.longitude ||
    (!state.config.door?.closingHours?.to &&
      !!state.config.door?.closingHours?.from) ||
    (!!state.config.door?.closingHours?.to &&
      !state.config.door?.closingHours?.from)
  );
};
